if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.pendingMediaId = null;
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.thumbnailItems().forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this.bindImageArrows();
        this.preloadAdjacentImages();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      thumbnailItems() {
        return this.elements.thumbnails ? Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')) : [];
      }

      bindImageArrows() {
        const previous = this.elements.thumbnails.querySelector('.slider-button--prev');
        const next = this.elements.thumbnails.querySelector('.slider-button--next');
        this.imageArrowButtons = { previous, next };

        const bind = (button, offset) => {
          if (!button) return;
          button.setAttribute('aria-label', offset < 0 ? 'Show previous product image' : 'Show next product image');
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();
              this.showRelativeMedia(offset);
            },
            true
          );
        };

        bind(previous, -1);
        bind(next, 1);
        this.imageArrowObserver = new MutationObserver(() => this.updateImageArrows());
        [previous, next].forEach((button) => {
          if (button) this.imageArrowObserver.observe(button, { attributes: true, attributeFilter: ['disabled'] });
        });
        this.updateImageArrows();
      }

      showRelativeMedia(offset) {
        const thumbnails = this.thumbnailItems();
        if (thumbnails.length < 2) return;

        const pendingIndex = this.pendingMediaId
          ? thumbnails.findIndex((item) => item.dataset.target === this.pendingMediaId)
          : -1;
        const currentIndex = thumbnails.findIndex((item) => item.querySelector('button[aria-current="true"]'));
        const startIndex = pendingIndex >= 0 ? pendingIndex : Math.max(currentIndex, 0);
        const targetIndex = Math.min(Math.max(startIndex + offset, 0), thumbnails.length - 1);
        if (targetIndex === startIndex) return;

        this.setActiveMedia(thumbnails[targetIndex].dataset.target, false);
      }

      updateImageArrows() {
        if (!this.imageArrowButtons) return;
        const thumbnails = this.thumbnailItems();
        const activeIndex = thumbnails.findIndex((item) => item.querySelector('button[aria-current="true"]'));
        const lastIndex = thumbnails.length - 1;

        const previousDisabled = activeIndex <= 0;
        const nextDisabled = activeIndex < 0 || activeIndex >= lastIndex;
        if (this.imageArrowButtons.previous && this.imageArrowButtons.previous.disabled !== previousDisabled) {
          this.imageArrowButtons.previous.disabled = previousDisabled;
        }
        if (this.imageArrowButtons.next && this.imageArrowButtons.next.disabled !== nextDisabled) {
          this.imageArrowButtons.next.disabled = nextDisabled;
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        this.pendingMediaId = activeMedia.dataset.mediaId;
        const image = activeMedia.querySelector('img');
        const activate = () => {
          if (this.pendingMediaId !== activeMedia.dataset.mediaId) return;
          this.pendingMediaId = null;
          this.activatePreparedMedia(activeMedia, prepend);
        };

        if (image && (!image.complete || image.naturalWidth === 0)) {
          image.loading = 'eager';
          image.fetchPriority = 'high';
          image.addEventListener('load', activate, { once: true });
          image.addEventListener('error', activate, { once: true });
          return;
        }

        activate();
      }

      activatePreparedMedia(activeMedia, prepend) {
        const mediaId = activeMedia.dataset.mediaId;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.preloadAdjacentImages();
      }

      preloadAdjacentImages() {
        const thumbnails = this.thumbnailItems();
        const activeIndex = thumbnails.findIndex((item) => item.querySelector('button[aria-current="true"]'));
        if (activeIndex < 0) return;

        [activeIndex - 1, activeIndex + 1].forEach((index) => {
          const target = thumbnails[index];
          if (!target) return;
          const media = this.elements.viewer.querySelector(`[data-media-id="${target.dataset.target}"]`);
          const image = media && media.querySelector('img');
          if (!image) return;
          image.loading = 'eager';
          if (typeof image.decode === 'function') image.decode().catch(() => {});
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.updateImageArrows();
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        window.requestAnimationFrame(() => this.updateImageArrows());
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
